import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, Download, FolderOpen, Scale } from "lucide-react";
import {
  Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import api, { apiError, downloadFile } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";
import { STATUS_CHART_COLORS, useLabels } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";

const COMPANY_COLORS = { b2b: "#1E2B68", outpatient: "#8CA63B" };
const PRIORITY_COLORS = { low: "#94A3B8", medium: "#0EA5E9", high: "#F97316", urgent: "#EF4444" };

export default function Kpi() {
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState("");
  const [mounted, setMounted] = useState(false);
  const { t } = useLanguage();
  const L = useLabels();

  useEffect(() => {
    api.get("/kpi/summary").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e, t("kpi.loadFail"))));
    setMounted(true);
  }, []);

  if (!data) {
    return (
      <div className="flex h-64 items-center justify-center" data-testid="kpi-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-delta-navy border-t-transparent" />
      </div>
    );
  }

  const doExport = async (kind) => {
    setExporting(kind);
    try {
      await downloadFile(`/export/${kind}`, kind === "kpi" ? "delta_kpi_report.xlsx" : "delta_cases_export.xlsx");
      toast.success(t("cases.exported"));
    } catch (e) {
      toast.error(apiError(e, t("cases.exportFail")));
    } finally {
      setExporting("");
    }
  };

  const stats = [
    { label: t("kpi.total"), value: data.total, icon: Scale, testid: "kpi-total" },
    { label: t("kpi.open"), value: data.open, icon: FolderOpen, testid: "kpi-open" },
    { label: t("kpi.resolved"), value: data.resolved, icon: CheckCircle2, testid: "kpi-resolved" },
    { label: t("kpi.escalated"), value: data.escalated_open, icon: AlertTriangle, testid: "kpi-escalated" },
    { label: t("kpi.avg"), value: data.avg_resolution_days != null ? t("kpi.days", { n: data.avg_resolution_days }) : "—", icon: Clock, testid: "kpi-avg-resolution" },
  ];

  const statusPie = Object.entries(data.by_status).map(([k, v]) => ({ name: L.status[k] || k, value: v, color: STATUS_CHART_COLORS[k] }));
  const companyPie = Object.entries(data.by_company).map(([k, v]) => ({ name: L.company[k] || k, value: v, color: COMPANY_COLORS[k] }));
  const priorityPie = Object.entries(data.by_priority).map(([k, v]) => ({ name: L.priority[k] || k, value: v, color: PRIORITY_COLORS[k] }));
  const monthLabel = (m) => new Date(`${m}-02`).toLocaleDateString(document.documentElement.lang === "ar" ? "ar" : "en", { month: "short", year: "2-digit" });

  return (
    <div className="space-y-5" data-testid="kpi-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">{t("kpi.title")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("kpi.subtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="export-cases-excel-button" disabled={!!exporting} onClick={() => doExport("cases")}
            className="border-delta-limeBorder text-delta-navy hover:bg-delta-limeLight">
            <Download className="me-2 h-4 w-4" /> {exporting === "cases" ? t("cases.exporting") : t("kpi.casesExcel")}
          </Button>
          <Button data-testid="export-kpi-excel-button" disabled={!!exporting} onClick={() => doExport("kpi")}
            className="bg-delta-navy hover:bg-delta-navyDark">
            <Download className="me-2 h-4 w-4" /> {exporting === "kpi" ? t("cases.exporting") : t("kpi.kpiExcel")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {stats.map((s, i) => (
          <Card key={s.label} data-testid={s.testid} className="animate-fade-up" style={{ animationDelay: `${i * 50}ms` }}>
            <CardContent className="p-4">
              <s.icon className="mb-2 h-5 w-5 text-delta-lime" />
              <p className="font-heading text-2xl font-bold text-slate-900">{s.value}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="lg:col-span-2" data-testid="kpi-monthly-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">{t("kpi.monthly")}</CardTitle>
          </CardHeader>
          <CardContent className="h-64 min-w-[200px]">
            {mounted && (
            <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={180}>
              <BarChart data={data.monthly} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip labelFormatter={monthLabel} />
                <Bar dataKey="count" name={t("kpi.cases")} fill="#1E2B68" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {[
          { title: t("kpi.byStatus"), data: statusPie, testid: "kpi-status-chart" },
          { title: t("kpi.byCompany"), data: companyPie, testid: "kpi-company-chart" },
          { title: t("kpi.byPriority"), data: priorityPie, testid: "kpi-priority-chart" },
        ].map((chart) => (
          <Card key={chart.title} data-testid={chart.testid}>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">{chart.title}</CardTitle>
            </CardHeader>
            <CardContent className="h-56 min-w-[200px]">
              {!mounted || chart.data.length === 0 ? (
                <p className="flex h-full items-center justify-center text-sm text-slate-400">{t("common.noData")}</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={180}>
                  <PieChart>
                    <Pie data={chart.data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={3}>
                      {chart.data.map((e) => <Cell key={e.name} fill={e.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
              <div className="-mt-4 flex flex-wrap justify-center gap-3">
                {chart.data.map((e) => (
                  <div key={e.name} className="flex items-center gap-1.5 text-xs text-slate-600">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: e.color }} />
                    {e.name} ({e.value})
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        <Card data-testid="kpi-workload-table">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">{t("kpi.workload")}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {data.lawyer_workload.length === 0 ? (
              <p className="px-6 py-8 text-center text-sm text-slate-400">{t("kpi.noAssignments")}</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50 text-start text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-6 py-2.5 font-semibold">{t("kpi.lawyer")}</th>
                    <th className="px-4 py-2.5 font-semibold">{t("kpi.assigned")}</th>
                    <th className="px-4 py-2.5 font-semibold">{t("kpi.openCol")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.lawyer_workload.map((l) => (
                    <tr key={l.name}>
                      <td className="px-6 py-3 font-medium text-slate-800">{l.name}</td>
                      <td className="px-4 py-3 text-slate-600">{l.total}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${l.open > 0 ? "bg-amber-100 text-amber-800" : "bg-green-50 text-green-700"}`}>
                          {l.open}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
